import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Cookie, Cake, Fish, Terminal, Trophy, Sparkles, Swords, Store, ArrowLeftRight, Star } from "lucide-react";

const CLASS_EMOJI = { archer: "🏹", assassin: "🗡️", tank: "🛡️", cleric: "✨" };

const BUILTINS = [
  ["!pancake <message>", "Talk to PancakeCupCake_ (AI)"],
  ["!hug", "Give her a hug — boosts her mood"],
  ["!givecookies <amount>", "Give her cookies (she may give back x2!)"],
  ["!givecupcakes <amount>", "Give her cupcakes (she may give back x2!)"],
  ["!bribe <amount> [cookies|cupcakes]", "Sweeten her up — she might share from her jar"],
  ["!stop", "Catch her sneaking someone's treats!"],
  ["!fish", "Go fishing 🎣"],
  ["!sellfish", "Sell your caught fish for treats"],
  ["!slots <amount>", "Play the slots 🎰"],
  ["!gamble <amount>", "Gamble your treats"],
  ["!cookies / !cupcakes", "Check your treat balance"],
  ["!nick <name>", "Set your nickname"],
  ["!commands", "Show this page"],
];
const RPG_CMDS = [
  ["!join <archer|cleric|tank|assassin>", "Join the dungeon battle as a class ⚔️"],
  ["!class <class>", "Switch your class"],
  ["!leave / !party", "Leave the fight / see the roster"],
  ["!heal <lowest|class>", "Cleric: choose who to prioritise healing"],
  ["!potion hp <%> / !potion mp <%>", "Auto-drink a potion below that %"],
  ["!attackpotion", "Drink an Attack Potion for a temporary ATK boost ⚗️"],
  ["!crate", "Spend cupcakes to summon a loot crate 🎁"],
  ["!gear", "See your equipped gear & bag"],
  ["!sprinkles", "Check your ✨ Sprinkles"],
  ["!cashout <✨> <cookies|cupcakes>", "Convert Sprinkles to treats"],
  ["!buysprinkles <amt> <cookies|cupcakes>", "Convert treats to Sprinkles"],
];

const SLOT_LABEL = {
  weapon: "Weapon", head: "Head", chest: "Chest", hands: "Hands", leggings: "Leggings",
  feet: "Feet", necklace: "Necklace", belt: "Belt", cape: "Cape", ring1: "Ring 1", ring2: "Ring 2",
};
const ARMOR_SLOTS = ["head", "chest", "hands", "leggings", "feet"];
const ACC_SLOTS = ["necklace", "belt", "cape", "ring1", "ring2"];
const RARITY = { common: "#9ca3af", rare: "#38bdf8", legendary: "#f59e0b" };
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : BACKEND + u) : "");
const Icon = ({ url, emoji, size = 18 }) => url ? <img src={mediaUrl(url)} alt="" className="object-contain shrink-0 inline-block align-middle" style={{ width: size, height: size }} /> : <span>{emoji}</span>;

function Board({ title, icon: Icon, color, rows, render }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3"><Icon className="w-5 h-5" style={{ color }} /><h3 className="font-heading font-800 text-lg">{title}</h3></div>
      {rows?.length ? (
        <ol className="space-y-1.5">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 truncate">
                <span className="w-5 text-center font-800" style={{ color: i < 3 ? color : "#666" }}>{i + 1}</span>
                <span className="truncate">{r.name}</span>
              </span>
              <span className="font-mono font-700 shrink-0 ml-2" style={{ color }}>{render(r)}</span>
            </li>
          ))}
        </ol>
      ) : <p className="text-sm text-muted-foreground">No entries yet — be the first!</p>}
    </div>
  );
}

function SlotCard({ slot, ch, gname, onUnequip }) {
  const gid = ch.equipped[slot];
  return (
    <div className="bg-secondary rounded-lg p-2.5" data-testid={`char-slot-${slot}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">{SLOT_LABEL[slot]}</div>
      <div className="text-sm truncate">{gname(gid)}</div>
      {gid && <button onClick={() => onUnequip(slot)} className="text-[10px] text-red-400 mt-0.5 hover:underline" data-testid={`char-unequip-${slot}`}>unequip</button>}
    </div>
  );
}

function CharacterEditor() {
  const [auth, setAuth] = useState(null);
  const [ch, setCh] = useState(null);
  const [store, setStore] = useState(null);
  const [trades, setTrades] = useState({ incoming: [], outgoing: [] });
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState("");
  const [tradeItem, setTradeItem] = useState(null);
  const [tradeTo, setTradeTo] = useState("");
  const [tradeWant, setTradeWant] = useState(0);
  const [showReq, setShowReq] = useState(false);
  const [reqTo, setReqTo] = useState("");
  const [reqWant, setReqWant] = useState("");
  const [reqGive, setReqGive] = useState("");
  const [reqSp, setReqSp] = useState(0);

  const loadChar = async (login) => {
    try {
      const r = await api.get(`/public/rpg/character/${encodeURIComponent(login)}`);
      if (r.data.found) setCh(r.data);
      else setMsg("You're logged in! Type !join <class> in chat to create your fighter, then reload.");
    } catch (e) { setMsg("Could not load character."); }
  };
  const loadStore = async () => { try { setStore((await api.get("/public/rpg/store")).data); } catch (e) {} };
  const loadTrades = async () => { try { setTrades((await api.get("/public/rpg/trades")).data); } catch (e) {} };
  const loadHistory = async () => { try { setHistory((await api.get("/public/rpg/history")).data.history || []); } catch (e) {} };

  useEffect(() => {
    api.get("/auth/twitch/me").then((r) => {
      setAuth(r.data);
      if (r.data.authenticated) { loadChar(r.data.login); loadStore(); loadTrades(); loadHistory(); }
    }).catch(() => setAuth({ authenticated: false, configured: false }));
    const p = new URLSearchParams(window.location.search).get("twitch");
    if (p && p !== "ok") setMsg("Twitch login didn't complete (" + p + "). Please try again.");
  }, []);

  const withCh = (data) => { if (data.found !== false) setCh(data); };
  const act = async (body) => {
    try { const r = await api.post("/public/rpg/equip", body); withCh(r.data); setMsg(""); }
    catch (e) { handleErr(e); }
  };
  const buy = async (item) => {
    const payload = item.kind === "attack_potion" ? { kind: "attack_potion" }
      : item.kind === "arrow" ? { kind: "arrow", arrow_id: item.arrow_id }
      : { kind: "gear", gear_id: item.gear_id };
    try { const r = await api.post("/public/rpg/buy", payload); withCh(r.data); setMsg(`Bought ${item.name}!`); }
    catch (e) { handleErr(e); }
  };
  const sell = async (g) => {
    try { const r = await api.post("/public/rpg/sell", { gear_id: g.id }); withCh(r.data); setMsg(`Sold ${g.name} for ${r.data.sold_for} ✨`); loadHistory(); }
    catch (e) { handleErr(e); }
  };
  const bulkSell = async (mode) => {
    try { const r = await api.post("/public/rpg/sell_bulk", { mode }); withCh(r.data); setMsg(`Bulk-sold ${r.data.sold_count} item(s) for ${r.data.sold_for} ✨`); loadHistory(); }
    catch (e) { handleErr(e); }
  };
  const createTrade = async () => {
    if (!tradeItem || !tradeTo.trim()) return;
    try {
      await api.post("/public/rpg/trade/create", { type: "offer", to_user: tradeTo.trim(), give_gear_id: tradeItem.id, want_sprinkles: Number(tradeWant) || 0 });
      setTradeItem(null); setTradeTo(""); setTradeWant(0); setMsg("Trade offer sent!");
      loadChar(auth.login); loadTrades();
    } catch (e) { handleErr(e); }
  };
  const createRequest = async () => {
    if (!reqWant || !reqTo.trim() || (!reqGive && (Number(reqSp) || 0) <= 0)) { setMsg("Pick what you want and offer an item and/or ✨."); return; }
    try {
      await api.post("/public/rpg/trade/create", { type: "request", to_user: reqTo.trim(), want_gear_id: reqWant, give_gear_id: reqGive || undefined, give_sprinkles: Number(reqSp) || 0 });
      setShowReq(false); setReqTo(""); setReqWant(""); setReqGive(""); setReqSp(0); setMsg("Request sent!");
      loadChar(auth.login); loadTrades();
    } catch (e) { handleErr(e); }
  };
  const acceptTrade = async (id) => { try { const r = await api.post("/public/rpg/trade/accept", { trade_id: id }); withCh(r.data); loadTrades(); loadHistory(); setMsg("Trade accepted!"); } catch (e) { handleErr(e); } };
  const cancelTrade = async (id) => { try { await api.post("/public/rpg/trade/cancel", { trade_id: id }); loadChar(auth.login); loadTrades(); } catch (e) { handleErr(e); } };
  const handleErr = (e) => {
    if (e.response?.status === 401) { setMsg("Session expired — please log in with Twitch again."); setAuth({ authenticated: false, configured: true }); }
    else setMsg(e.response?.data?.detail || "Action failed");
  };
  const logout = async () => { try { await api.post("/auth/twitch/logout"); } catch (e) {} setAuth({ authenticated: false, configured: true }); setCh(null); setStore(null); };

  const cls = ch && ch.classes.find((c) => c.id === ch.cls);
  const gname = (gid) => { const g = ch?.gear_catalog.find((x) => x.id === gid); return g ? `${g.emoji} ${g.name}` : "—"; };
  const canAfford = (price) => ch && ch.sprinkles >= price;

  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 mb-6" data-testid="character-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Swords className="w-5 h-5 text-primary" /><h3 className="font-heading font-800 text-lg">Your Dungeon Character</h3></div>
        {auth?.authenticated && (
          <div className="flex items-center gap-2 text-sm">
            {ch && <span className="font-mono font-700 text-[#a855f7]" data-testid="char-sprinkles">✨ {ch.sprinkles}</span>}
            <span className="text-muted-foreground">@{auth.login}</span>
            <Button variant="outline" size="sm" className="border-white/10" onClick={logout} data-testid="char-logout">Log out</Button>
          </div>
        )}
      </div>

      {auth && !auth.authenticated && (
        <div className="text-center py-4">
          {auth.configured === false ? (
            <p className="text-sm text-amber-400" data-testid="twitch-unconfigured">Twitch login isn't set up yet — the streamer needs to add Twitch app keys.</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">Log in with Twitch to equip gear, shop, trade and tune your fighter. Only you can edit your own character.</p>
              <a href={`${BACKEND}/api/auth/twitch/login`} data-testid="twitch-login-btn">
                <Button className="bg-[#9146FF] hover:bg-[#7c2ff0] text-white font-700">Login with Twitch</Button>
              </a>
            </>
          )}
        </div>
      )}
      {msg && <p className="text-sm text-amber-400 mb-3" data-testid="char-msg">{msg}</p>}

      {ch && (
        <div className="space-y-5" data-testid="char-panel">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-2xl">{cls?.emoji}</span>
            <span className="font-700 capitalize">{ch.cls}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${ch.active ? "bg-green-500/20 text-green-400" : "bg-white/10 text-muted-foreground"}`}>{ch.active ? "in battle" : "resting"}</span>
            {ch.consumables?.attack_potion > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300" data-testid="char-atkpot-count">⚗️ Attack Potion x{ch.consumables.attack_potion}</span>}
          </div>
          <p className="text-[11px] text-muted-foreground">This <span className="capitalize font-700">{ch.cls}</span> can wield: <b>{ch.allowed_weapons.join(", ") || "—"}</b> · prefers <b className="capitalize">{ch.armor_pref}</b> armor. Wearing off-type armor changes your stats. Wear all 5 armor pieces of one type for a set bonus!</p>
          {ch.set_bonus && (
            <div className="text-xs px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 font-700" data-testid="char-setbonus">
              ✨ Set bonus active: <span className="capitalize">{ch.set_bonus.type}</span> set{ch.set_bonus.matched ? " (class-matched — bigger bonus!)" : ""} — {Object.entries(ch.set_bonus.stats || {}).filter(([, v]) => v).map(([k, v]) => `+${v} ${k}`).join(", ") || "bonus"}
            </div>
          )}

          {/* Equipped slots */}
          <div>
            <div className="text-xs font-700 mb-2">⚔️ Equipped</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SlotCard slot="weapon" ch={ch} gname={gname} onUnequip={(s) => act({ unequip_slot: s })} />
            </div>
            <div className="text-[10px] uppercase text-muted-foreground mt-3 mb-1">Armor</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {ARMOR_SLOTS.map((s) => <SlotCard key={s} slot={s} ch={ch} gname={gname} onUnequip={(sl) => act({ unequip_slot: sl })} />)}
            </div>
            <div className="text-[10px] uppercase text-muted-foreground mt-3 mb-1">Accessories</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {ACC_SLOTS.map((s) => <SlotCard key={s} slot={s} ch={ch} gname={gname} onUnequip={(sl) => act({ unequip_slot: sl })} />)}
            </div>
          </div>

          {/* Inventory: equip / sell / trade */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-700">🎒 Your bag</div>
              {ch.inventory.length > 0 && (
                <div className="flex gap-1.5">
                  <button onClick={() => bulkSell("duplicates")} className="text-[10px] px-2 py-1 rounded bg-[#a855f7]/20 text-[#c084fc] hover:bg-[#a855f7]/30" data-testid="char-bulksell-duplicates">Sell duplicates</button>
                  <button onClick={() => bulkSell("commons")} className="text-[10px] px-2 py-1 rounded bg-[#a855f7]/20 text-[#c084fc] hover:bg-[#a855f7]/30" data-testid="char-bulksell-commons">Sell all commons</button>
                </div>
              )}
            </div>
            {ch.inventory.length ? (
              <div className="space-y-1.5">
                {ch.inventory.map((g) => (
                  <div key={g.id} className="flex items-center justify-between gap-2 bg-secondary rounded-lg px-3 py-2 border-l-4" style={{ borderColor: RARITY[g.rarity] || "#333" }} data-testid={`char-invrow-${g.id}`}>
                    <div className="text-sm truncate">
                      <Icon url={g.image_url} emoji={g.emoji} /> {g.name} <span className="text-muted-foreground">x{g.count}</span>
                      <span style={{ color: RARITY[g.rarity] }} className="capitalize text-[10px] ml-1">{g.rarity}</span>
                      {g.slot === "weapon" && !g.can_equip && <span className="text-[10px] text-red-400 ml-1">· class can't use</span>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => g.can_equip && act({ equip_gear: g.id })} disabled={!g.can_equip} className={`text-[11px] px-2 py-1 rounded ${g.can_equip ? "bg-primary text-white hover:brightness-110" : "bg-white/5 text-muted-foreground cursor-not-allowed"}`} data-testid={`char-equip-${g.id}`}>Equip</button>
                      <button onClick={() => sell(g)} className="text-[11px] px-2 py-1 rounded bg-[#a855f7]/20 text-[#c084fc] hover:bg-[#a855f7]/30" data-testid={`char-sell-${g.id}`}>Sell {g.sell_value}✨</button>
                      <button onClick={() => { setTradeItem(g); setTradeTo(""); setTradeWant(0); }} className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20" data-testid={`char-trade-${g.id}`}>Trade</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Empty — open crates with <code className="text-primary">!crate</code> or buy from the store below.</p>}
          </div>

          {/* Trade offer form */}
          {tradeItem && (
            <div className="bg-secondary rounded-lg p-3 border border-white/10" data-testid="char-trade-form">
              <div className="text-xs font-700 mb-2 flex items-center gap-1"><ArrowLeftRight className="w-3.5 h-3.5" /> Trade away {tradeItem.emoji} {tradeItem.name}</div>
              <div className="flex flex-wrap items-end gap-2">
                <div><div className="text-[10px] text-muted-foreground mb-1">Send to (Twitch name)</div><Input value={tradeTo} onChange={(e) => setTradeTo(e.target.value)} placeholder="viewer_name" className="bg-[#0a0a0a] border-white/10 h-8 w-40" data-testid="char-trade-to" /></div>
                <div><div className="text-[10px] text-muted-foreground mb-1">Ask ✨ (optional)</div><Input type="number" value={tradeWant} onChange={(e) => setTradeWant(e.target.value)} className="bg-[#0a0a0a] border-white/10 h-8 w-28" data-testid="char-trade-want" /></div>
                <Button size="sm" className="bg-primary h-8" onClick={createTrade} data-testid="char-trade-send">Send offer</Button>
                <Button size="sm" variant="outline" className="border-white/10 h-8" onClick={() => setTradeItem(null)}>Cancel</Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">The item is held safely until they accept or the offer is cancelled.</p>
            </div>
          )}

          {/* Incoming / outgoing trades */}
          {(trades.incoming.length > 0 || trades.outgoing.length > 0) && (
            <div data-testid="char-trades">
              <div className="text-xs font-700 mb-2">🤝 Trade offers</div>
              <div className="space-y-1.5">
                {trades.incoming.map((t) => {
                  const gtxt = (g, sp) => { const p = []; if (g) p.push(`${g.emoji} ${g.name}`); if (sp > 0) p.push(`${sp}✨`); return p.join(" + ") || "nothing"; };
                  return (
                    <div key={t.id} className="flex items-center justify-between gap-2 bg-secondary rounded-lg px-3 py-2 text-sm" data-testid={`char-trade-incoming-${t.id}`}>
                      <span className="truncate"><span className="text-[9px] uppercase text-muted-foreground mr-1">{t.type}</span><b>@{t.from_user}</b> gives {gtxt(t.give_gear, t.give_sprinkles)} ⇄ wants {gtxt(t.want_gear, t.want_sprinkles)} from you</span>
                      <div className="flex gap-1.5 shrink-0">
                        <button onClick={() => acceptTrade(t.id)} className="text-[11px] px-2 py-1 rounded bg-green-600 text-white" data-testid={`char-trade-accept-${t.id}`}>Accept</button>
                        <button onClick={() => cancelTrade(t.id)} className="text-[11px] px-2 py-1 rounded bg-white/10">Decline</button>
                      </div>
                    </div>
                  );
                })}
                {trades.outgoing.map((t) => {
                  const gtxt = (g, sp) => { const p = []; if (g) p.push(`${g.emoji} ${g.name}`); if (sp > 0) p.push(`${sp}✨`); return p.join(" + ") || "nothing"; };
                  return (
                    <div key={t.id} className="flex items-center justify-between gap-2 bg-secondary rounded-lg px-3 py-2 text-sm text-muted-foreground" data-testid={`char-trade-outgoing-${t.id}`}>
                      <span className="truncate"><span className="text-[9px] uppercase mr-1">{t.type}</span>To <b>@{t.to_user}</b>: give {gtxt(t.give_gear, t.give_sprinkles)} ⇄ want {gtxt(t.want_gear, t.want_sprinkles)} — pending</span>
                      <button onClick={() => cancelTrade(t.id)} className="text-[11px] px-2 py-1 rounded bg-white/10 shrink-0">Cancel</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Request an item (two-way swap) */}
          <div data-testid="char-request">
            <button onClick={() => setShowReq((s) => !s)} className="text-xs font-700 flex items-center gap-1 text-cookie hover:brightness-110" data-testid="char-request-toggle"><ArrowLeftRight className="w-3.5 h-3.5" /> Request an item from another player</button>
            {showReq && (
              <div className="bg-secondary rounded-lg p-3 border border-white/10 mt-2" data-testid="char-request-form">
                <div className="flex flex-wrap items-end gap-2">
                  <div><div className="text-[10px] text-muted-foreground mb-1">From (Twitch name)</div><Input value={reqTo} onChange={(e) => setReqTo(e.target.value)} placeholder="viewer_name" className="bg-[#0a0a0a] border-white/10 h-8 w-36" data-testid="req-to" /></div>
                  <div><div className="text-[10px] text-muted-foreground mb-1">Item you want</div>
                    <select value={reqWant} onChange={(e) => setReqWant(e.target.value)} className="bg-[#0a0a0a] border border-white/10 rounded h-8 text-xs px-1 w-44" data-testid="req-want">
                      <option value="">pick item…</option>
                      {ch.gear_catalog.map((g) => <option key={g.id} value={g.id}>{g.emoji} {g.name} ({g.rarity})</option>)}
                    </select>
                  </div>
                  <div><div className="text-[10px] text-muted-foreground mb-1">You give (item)</div>
                    <select value={reqGive} onChange={(e) => setReqGive(e.target.value)} className="bg-[#0a0a0a] border border-white/10 rounded h-8 text-xs px-1 w-40" data-testid="req-give">
                      <option value="">none</option>
                      {ch.inventory.map((g) => <option key={g.id} value={g.id}>{g.emoji} {g.name} (x{g.count})</option>)}
                    </select>
                  </div>
                  <div><div className="text-[10px] text-muted-foreground mb-1">+ ✨</div><Input type="number" value={reqSp} onChange={(e) => setReqSp(e.target.value)} className="bg-[#0a0a0a] border-white/10 h-8 w-24" data-testid="req-sp" /></div>
                  <Button size="sm" className="bg-primary h-8" onClick={createRequest} data-testid="req-send">Send request</Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Your offered item &amp; ✨ are held safely until they accept or you cancel.</p>
              </div>
            )}
          </div>

          {/* Trade & sale history */}
          {history.length > 0 && (
            <div data-testid="char-history">
              <div className="text-xs font-700 mb-2">📜 Recent deals</div>
              <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] text-muted-foreground bg-secondary rounded px-2 py-1" data-testid={`char-history-${i}`}>
                    <span className={h.kind === "sale" ? "text-[#c084fc]" : "text-sky-300"}>{h.kind === "sale" ? "💰" : "🤝"}</span>
                    <span className="truncate">{h.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Store */}
          {store?.enabled && (
            <div data-testid="char-store">
              <div className="text-xs font-700 mb-2 flex items-center gap-1"><Store className="w-3.5 h-3.5 text-cookie" /> Store <span className="text-[10px] text-muted-foreground font-400">spend ✨ Sprinkles</span>{store.discount_pct > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 font-700 ml-1">🔥 Daily {store.discount_pct}% off featured</span>}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {store.items.map((it, i) => {
                  const id = it.kind === "attack_potion" ? "attack_potion" : it.kind === "arrow" ? it.arrow_id : it.gear_id;
                  const bc = it.kind === "attack_potion" ? "#ef4444" : it.kind === "arrow" ? "#22d3ee" : (RARITY[it.rarity] || "#333");
                  return (
                    <div key={id + i} className="flex items-center justify-between gap-2 bg-secondary rounded-lg px-3 py-2 text-sm border-l-4" style={{ borderColor: bc }} data-testid={`store-item-${id}`}>
                      <span className="truncate"><Icon url={it.image_url} emoji={it.emoji} /> {it.name}{it.featured && <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/30 text-red-200 ml-1 font-700">SALE</span>}{it.kind === "attack_potion" ? ` (ATK x${it.mult}, ${it.duration}s)` : ""}{it.kind === "arrow" ? ` 🏹 ${it.effect !== "none" ? it.effect : `+${it.atk_bonus} ATK`}` : ""}</span>
                      <button onClick={() => buy(it)} disabled={!canAfford(it.price)} className={`text-[11px] px-2 py-1 rounded shrink-0 ${canAfford(it.price) ? "bg-cookie text-black font-700 hover:brightness-110" : "bg-white/5 text-muted-foreground cursor-not-allowed"}`} data-testid={`store-buy-${id}`}>{it.featured && it.orig_price ? <span className="line-through opacity-60 mr-1">{it.orig_price}</span> : null}Buy {it.price}✨</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {ch.cls === "archer" && ch.arrows?.length > 0 && (
            <div data-testid="char-arrows">
              <div className="text-xs font-700 mb-2">🏹 Arrows</div>
              <div className="flex flex-wrap gap-2">
                {ch.arrows.map((a) => (
                  <button key={a.id} onClick={() => a.owned ? act({ arrow: a.id }) : setMsg(`Buy ${a.name} in the store below (${a.price}✨) to use it.`)}
                    className={`text-xs px-3 py-1.5 rounded-lg border text-left ${ch.arrow === a.id ? "bg-primary text-white border-primary" : a.owned ? "bg-[#0a0a0a] border-white/10 text-muted-foreground" : "bg-[#0a0a0a] border-white/10 text-muted-foreground/50"}`}
                    data-testid={`char-arrow-${a.id}`}>
                    <div className="font-700">{a.owned ? "" : "🔒 "}{a.emoji} {a.name}</div>
                    <div className="text-[10px] opacity-80">{a.owned ? `${a.atk_bonus > 0 ? `+${a.atk_bonus} ATK` : "no bonus"}${a.effect && a.effect !== "none" ? ` · ${a.effect}` : ""}` : `buy for ${a.price}✨`}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {ch.abilities?.length > 0 && (
            <div data-testid="char-abilities-wrap">
            <div className="mb-3" data-testid="char-level">
              <div className="flex items-center justify-between text-xs mb-1"><span className="font-700">⭐ Level {ch.level}</span><span className="text-muted-foreground">{ch.xp} / {ch.xp_next} XP</span></div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${Math.min(100, (ch.xp / Math.max(1, ch.xp_next)) * 100)}%` }} /></div>
            </div>
            {ch.attacks?.some((a) => a.mp_cost > 0) && (
              <div className="mb-3" data-testid="char-specials">
                <div className="text-xs font-700 mb-2">⚡ Special attacks <span className="text-[10px] text-muted-foreground font-400">equip up to 4 · set proc %</span></div>
                <div className="space-y-1.5">
                  {ch.attacks.filter((a) => a.mp_cost > 0).map((a) => {
                    const cur = (ch.specials || []).find((s) => s.id === a.id);
                    const on = !!cur;
                    const setSp = (pct, equip) => {
                      let list = (ch.specials || []).filter((s) => s.id !== a.id);
                      if (equip) list = [...list, { id: a.id, pct }].slice(0, 4);
                      act({ specials: list });
                    };
                    return (
                      <div key={a.id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm ${a.unlocked ? "bg-secondary" : "bg-secondary/40 opacity-60"}`} data-testid={`char-special-${a.id}`}>
                        <span className="flex items-center gap-1.5 truncate">
                          {!a.unlocked && <span>🔒</span>}
                          {a.image_url
                            ? <img src={a.image_url.startsWith("http") ? a.image_url : BACKEND + a.image_url} alt="" className="w-5 h-5 object-contain shrink-0" data-testid={`char-special-img-${a.id}`} />
                            : <span className="shrink-0">{a.emoji}</span>}
                          <span className="truncate">{a.name} <span className="text-[10px] text-muted-foreground">Lv{a.min_level} · {a.mp_cost}MP · x{a.dmg_mult}{a.aoe ? " · AOE" : ""}</span></span>
                        </span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {on && <input type="number" min="0" max="100" value={cur.pct} onChange={(e) => setSp(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)), true)} className="w-14 h-7 bg-[#0a0a0a] border border-white/10 rounded text-xs px-1 text-center" data-testid={`char-special-pct-${a.id}`} />}
                          <button disabled={!a.unlocked} onClick={() => setSp(cur?.pct ?? 25, !on)} className={`text-[11px] px-2 py-1 rounded ${on ? "bg-primary text-white" : a.unlocked ? "bg-white/10 hover:bg-white/20" : "bg-white/5 cursor-not-allowed"}`} data-testid={`char-special-toggle-${a.id}`}>{on ? "%" : a.unlocked ? "Equip" : `Lv${a.min_level}`}</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
              <div className="text-xs font-700 mb-2">⭐ Signature move</div>
              <div className="flex flex-wrap gap-2">
                {ch.abilities.map((a) => (
                  <button key={a.id} onClick={() => act({ ability: a.id })} title={a.desc}
                    className={`text-xs px-3 py-1.5 rounded-lg border text-left ${ch.ability === a.id ? "bg-primary text-white border-primary" : "bg-[#0a0a0a] border-white/10 text-muted-foreground"}`}
                    data-testid={`char-ability-${a.id}`}>
                    <div className="font-700">{a.name}</div>
                    <div className="text-[10px] opacity-80">{a.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-secondary rounded-lg p-3">
              <div className="text-xs font-700 mb-2">🧪 Auto-potions</div>
              <div className="flex flex-wrap gap-1.5">
                {ch.potion_types.map((p) => {
                  const sel = (p.kind === "hp" ? ch.hp_potion : ch.mp_potion) === p.id;
                  return <button key={p.id} onClick={() => act({ [p.kind === "hp" ? "hp_potion" : "mp_potion"]: p.id })} className={`text-xs px-2 py-1 rounded-full border flex items-center gap-1 ${sel ? "bg-primary text-white border-primary" : "bg-[#0a0a0a] border-white/10 text-muted-foreground"}`} data-testid={`char-potion-${p.id}`}>{p.image_url ? <img src={p.image_url.startsWith("http") ? p.image_url : BACKEND + p.image_url} alt="" className="w-4 h-4 object-contain" data-testid={`char-potion-img-${p.id}`} /> : <span>{p.emoji}</span>} {p.name}</button>;
                })}
              </div>
            </div>
            {ch.cls === "cleric" && (
              <div className="bg-secondary rounded-lg p-3">
                <div className="text-xs font-700 mb-2">✨ Heal priority</div>
                <div className="flex flex-wrap gap-1.5">
                  {["lowest", "archer", "cleric", "tank", "assassin"].map((m) => (
                    <button key={m} onClick={() => act({ heal_mode: m })} className={`text-xs px-2 py-1 rounded-full border capitalize ${ch.heal_mode === m ? "bg-sky-500 text-white border-sky-400" : "bg-[#0a0a0a] border-white/10 text-muted-foreground"}`} data-testid={`char-heal-${m}`}>{m}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicCommands() {
  const [data, setData] = useState({ custom_commands: [], leaderboards: { cookies: [], cupcakes: [], fishing: [], sprinkles: [] } });
  const [levels, setLevels] = useState([]);
  useEffect(() => {
    api.get("/public/commands").then((r) => setData(r.data)).catch(() => {});
    api.get("/public/rpg/leaderboard/levels").then((r) => setLevels(r.data.levels || [])).catch(() => {});
  }, []);
  const lb = data.leaderboards || {};

  return (
    <div className="dark min-h-screen bg-background text-foreground" data-testid="public-commands-page">
      <div className="max-w-4xl mx-auto px-5 py-12">
        <header className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center"><Cake className="w-6 h-6 text-white" /></div>
            <span className="font-heading font-800 text-2xl">PancakeCupCake_</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-800 tracking-tight">Commands & Leaderboards</h1>
          <p className="text-muted-foreground mt-2 text-sm">Type these in chat to play with PancakeCupCake_ 💖</p>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <Board title="🍪 Cookies" icon={Cookie} color="#f0b429" rows={lb.cookies} render={(r) => r.value} />
          <Board title="🧁 Cupcakes" icon={Cake} color="#FF3366" rows={lb.cupcakes} render={(r) => r.value} />
          <Board title="🎣 Biggest Catch" icon={Fish} color="#38bdf8" rows={lb.fishing} render={(r) => `${r.emoji} ${r.size}cm`} />
          <Board title="✨ Sprinkles" icon={Sparkles} color="#a855f7" rows={lb.sprinkles} render={(r) => r.value} />
          <Board title="⭐ Levels" icon={Star} color="#fbbf24" rows={levels} render={(r) => `${CLASS_EMOJI[r.cls] || "⚔️"} Lv ${r.level}`} />
        </div>

        <CharacterEditor />

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4"><Swords className="w-5 h-5 text-primary" /><h3 className="font-heading font-800 text-lg">Dungeon Battle Commands</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {RPG_CMDS.map(([c, d]) => (
              <div key={c} className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5">
                <code className="text-primary text-sm font-mono">{c}</code>
                <span className="text-xs text-muted-foreground text-right">{d}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4"><Terminal className="w-5 h-5 text-primary" /><h3 className="font-heading font-800 text-lg">Commands</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {BUILTINS.map(([c, d]) => (
              <div key={c} className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5">
                <code className="text-cookie text-sm font-mono">{c}</code>
                <span className="text-xs text-muted-foreground text-right">{d}</span>
              </div>
            ))}
          </div>
        </div>

        {data.custom_commands?.length > 0 && (
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-cookie" /><h3 className="font-heading font-800 text-lg">Channel Commands</h3></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {data.custom_commands.map((c) => (
                <div key={c.trigger} className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5">
                  <code className="text-primary text-sm font-mono">{c.trigger}</code>
                  <span className="text-xs text-muted-foreground text-right truncate">{c.response}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
